import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import LaptopIcon from '@mui/icons-material/Laptop';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { fetchStaffSessions, revokeAllStaffSessions, revokeStaffSession } from './staffApi';
import { StaffMember, StaffSession, StaffTarget } from './types';
import { formatInstant } from './staffUtils';

interface StaffSessionsPanelProps {
  target: StaffTarget;
  member: StaffMember;
  /** Сессии изменились: онлайн-статус в списке точки нужно обновить */
  onChanged: () => void;
}

const panelSx = {
  p: 2.5,
  borderRadius: '16px',
  bgcolor: '#ffffff',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
};

const smallChipSx = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

/** Сессии сотрудника: вкладка видна только при праве на сессии, оно же разрешает их завершать */
const StaffSessionsPanel: React.FC<StaffSessionsPanelProps> = ({ target, member, onChanged }) => {
  const [sessions, setSessions] = useState<StaffSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSessions(await fetchStaffSessions(target, member.id));
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось загрузить сессии'));
    } finally {
      setLoading(false);
    }
  }, [target, member.id]);

  useEffect(() => { load(); }, [load]);

  const activeCount = sessions.filter(s => !s.isRevoked).length;

  const handleRevokeAll = async () => {
    setRevokingAll(true);
    try {
      notify.success(await revokeAllStaffSessions(target, member.id));
      await load();
      onChanged();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось завершить сессии'));
    } finally {
      setRevokingAll(false);
    }
  };

  const handleRevoke = async (sessionId: number) => {
    setRevokingId(sessionId);
    try {
      notify.success(await revokeStaffSession(target, member.id, sessionId));
      await load();
      onChanged();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось завершить сессию'));
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Paper elevation={0} sx={{ ...panelSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: member.isOnline ? '#10b981' : '#94a3b8',
              boxShadow: member.isOnline ? '0 0 8px #10b981' : 'none'
            }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {member.isOnline ? 'Сотрудник сейчас онлайн' : 'Сотрудник сейчас офлайн'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {member.isOnline
                ? `Текущий раздел: ${member.currentSection || 'Кабинет'}`
                : member.lastActivityAt
                  ? `Последний визит: ${formatInstant(member.lastActivityAt)}`
                  : 'Данных о недавней активности нет'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={load}
            disabled={loading}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Обновить
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            disableElevation
            startIcon={revokingAll ? <CircularProgress size={16} color="inherit" /> : <BlockIcon sx={{ fontSize: 16 }} />}
            onClick={handleRevokeAll}
            disabled={revokingAll || activeCount === 0}
            sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#e11d48' }}
          >
            {revokingAll ? 'Сброс...' : 'Сбросить все сессии'}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...panelSx, p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
          Устройства и входы ({sessions.length})
        </Typography>

        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>{error}</Alert>
        ) : sessions.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>Сотрудник ещё не входил в портал</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sessions.map((sess) => (
              <Box
                key={sess.id}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: sess.isRevoked ? '#f1f5f9' : (sess.isOnline ? '#a7f3d0' : '#e2e8f0'),
                  bgcolor: sess.isRevoked ? '#f8fafc' : (sess.isOnline ? '#f0fdf4' : '#ffffff'),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  opacity: sess.isRevoked ? 0.65 : 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '10px',
                      bgcolor: sess.isOnline ? '#dcfce7' : '#f1f5f9',
                      color: sess.isOnline ? '#15803d' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <LaptopIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {sess.deviceInfo || 'Устройство'}
                      </Typography>
                      {sess.isOnline && <Chip label="Онлайн" size="small" sx={{ ...smallChipSx, fontWeight: 700, bgcolor: '#10b981', color: '#fff' }} />}
                      {sess.isRevoked && (
                        <Chip
                          label={sess.revokedReason === 'password_changed' ? 'Смена пароля' : 'Завершена'}
                          size="small"
                          sx={{ ...smallChipSx, bgcolor: '#f1f5f9', color: '#64748b' }}
                        />
                      )}
                      {sess.impersonatedBy && <Chip label="Вход админа" size="small" sx={{ ...smallChipSx, fontWeight: 700, bgcolor: '#fef3c7', color: '#b45309' }} />}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.3 }}>
                      IP: <strong>{sess.ipAddress || '-'}</strong> • Раздел: <strong>{sess.currentSection || '/'}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Вход: {formatInstant(sess.createdAt)} • Активность: {formatInstant(sess.lastActivityAt)}
                    </Typography>
                  </Box>
                </Box>

                {!sess.isRevoked && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={revokingId === sess.id || revokingAll}
                    onClick={() => handleRevoke(sess.id)}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '0.78rem' }}
                  >
                    {revokingId === sess.id ? 'Сброс...' : 'Завершить'}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default StaffSessionsPanel;
